import { ItemInterface, ItemIds } from "../interfaces/ItemInterface";

/**
 * Apply the real international (DHL) rate onto the principal shipping line of a
 * rate-engine item list, and drop the office-delivery line (it does not apply
 * to international shipments).
 *
 * The principal line is identified by the backend `isItemBase` flag — the same
 * marker the rest of the app uses for the "main item" (see the `mainItem`
 * lookups in shipmentServices). Its catalog id varies by weight bracket, so the
 * previous approach of matching hardcoded ids ("24" / "18") silently failed for
 * heavier shipments (>= 30 kg): the DHL quote was never copied in and the
 * rate-engine estimate was shown instead. The legacy ids are kept only as a
 * fallback, in case the backend ever returns the principal line without the
 * `isItemBase` flag set.
 *
 * Pure: returns a new array and does not mutate the input or its items.
 */
export const applyInternationalRate = (
  items: ItemInterface[],
  rate: ItemInterface["rate"]
): ItemInterface[] => {
  const withoutDelivery = items.filter((item) => item.id !== ItemIds.DELIVERY);

  let baseIndex = withoutDelivery.findIndex((item) => item.isItemBase);
  if (baseIndex === -1) {
    // Legacy safety net: principal DHL line catalog ids relied upon before
    // isItemBase was used.
    baseIndex = withoutDelivery.findIndex(
      (item) => item.id === "24" || item.id === "18"
    );
  }

  if (baseIndex === -1) {
    return withoutDelivery;
  }

  const updated = [...withoutDelivery];
  updated[baseIndex] = { ...updated[baseIndex], rate };
  return updated;
};
